import type { AssistantAnswer } from '@aurora-pvi/contracts';
import { useNavigate } from 'react-router-dom';

/**
 * An answer, and everything needed to disagree with it.
 *
 * The citations are not decoration and are not an afterthought below the fold:
 * they are the reason this feature is allowed to exist in a product whose whole
 * claim is that a figure can be argued with. Each one names the metric, the
 * value as the screens format it, the period it covers, the version of the
 * definition that produced it and the view it came from — so the reader can go
 * and look rather than take the paragraph on trust.
 *
 * Three states, and the second two matter more than the first:
 *
 * A **refusal** renders as a plain statement with no figures and no apology.
 * "This platform does not record staff attrition" is the correct answer to a
 * question about staff attrition, and dressing it up as an error would push a
 * reader toward asking somewhere that will guess.
 *
 * An **unverified citation** — a metric id the answer quoted that no view
 * returned — is surfaced, loudly. It is the earliest visible symptom of an
 * invented figure, and a reader is entitled to know before they act on the
 * sentence around it rather than after.
 */
export function AssistantAnswerPanel({
  answer,
  onFollow,
}: {
  answer: AssistantAnswer;
  onFollow: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="flex min-w-0 flex-col gap-2.5 px-2 py-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--g-text-faint)]">
          {answer.refused ? 'Not answerable' : 'Answer'}
        </span>
        <span className="text-[10.5px] text-[var(--g-text-faint)]">{answer.scopeLabel}</span>
      </div>

      <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-[var(--g-text)]">
        {answer.refused ? answer.refusalReason : answer.answer}
      </p>

      {answer.unverifiedCitations.length > 0 ? (
        <p
          className="rounded-[9px] px-2 py-1.5 text-[11.5px] leading-relaxed"
          style={{
            background: 'color-mix(in oklab, var(--g-critical) 12%, transparent)',
            color: 'var(--g-text-secondary)',
          }}
        >
          This answer named {answer.unverifiedCitations.join(', ')}, which no view returned. Treat
          anything attached to {answer.unverifiedCitations.length === 1 ? 'it' : 'them'} as
          unchecked and read the figure on its own screen.
        </p>
      ) : null}

      {answer.citations.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--g-text-faint)]">
            Every figure above, and where it came from
          </span>
          {answer.citations.map((citation) => (
            <button
              key={citation.metricId}
              type="button"
              disabled={!citation.route}
              onMouseDown={(event) => {
                event.preventDefault();
                if (!citation.route) return;
                onFollow();
                navigate(citation.route.startsWith('/v2') ? citation.route : '/v2/executive');
              }}
              className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-[9px] px-2 py-1 text-left hover:bg-[var(--g-surface-2)] disabled:cursor-default disabled:hover:bg-transparent"
            >
              <span className="text-[12px] text-[var(--g-text)]">{citation.label}</span>
              <span className="text-[12px] tabular-nums text-[var(--g-text-secondary)]">
                {citation.value}
              </span>
              <span className="text-[10.5px] text-[var(--g-text-faint)]">
                {citation.metricId} · {citation.definitionVersion} · {citation.periodStart} to{' '}
                {citation.periodEnd} · {citation.source}
              </span>
              {citation.unavailableReason ? (
                <span className="w-full text-[10.5px] text-[var(--g-text-muted)]">
                  {citation.unavailableReason}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {answer.maskedFields.length > 0 ? (
        <span className="text-[10.5px] text-[var(--g-text-muted)]">
          Figures withheld from you by your entitlement were not used: {answer.maskedFields.join(', ')}.
        </span>
      ) : null}

      <span className="text-[10.5px] text-[var(--g-text-faint)]">
        {answer.toolsUsed.length === 0
          ? 'No view was read to answer this.'
          : `Read from ${answer.toolsUsed.length} governed ${answer.toolsUsed.length === 1 ? 'view' : 'views'}. Nothing here was calculated by the assistant.`}
      </span>
    </div>
  );
}
